import { useRef, type FC } from 'react';
import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime, Settings } from 'luxon';

Settings.defaultLocale = 'pt-BR';

export interface DatePickerProps extends MuiDatePickerProps<DateTime> {
  field: {
    onChange: (value: string | null) => void;
    onBlur?: () => void;
    [key: string]: unknown;
  };
  value: DateTime | null;
  slotProps?: MuiDatePickerProps<DateTime>['slotProps'];
}

export const DatePicker: FC<DatePickerProps> = ({
  field,
  value,
  slotProps = {},
  ...props
}) => {
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const handleBlur = () => {
    if (pickerRef.current) {
      const activeElement = document.activeElement;
      if (!pickerRef.current.contains(activeElement)) {
        field.onBlur?.();
      }
    }
  };

  const handleDateChange = (newValue: DateTime | null) => {
    const formattedValue =
      newValue && newValue.isValid ? newValue.toFormat('dd/MM/yyyy') : null;

    field.onChange(formattedValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-BR">
      <div ref={pickerRef} onBlur={handleBlur}>
        <MuiDatePicker
          {...field}
          {...props}
          value={value}
          sx={{ width: '100%' }}
          maxDate={DateTime.now()}
          onChange={handleDateChange}
          slotProps={{
            ...slotProps,
            yearButton: { sx: { fontSize: 'inherit', lineHeight: 'unset' } },
          }}
          format="dd/MM/yyyy"
          localeText={{ toolbarTitle: '' }}
        />
      </div>
    </LocalizationProvider>
  );
};
